// Button.js - Eine wiederverwendbare Button-Komponente
import React from 'react';

/**
 * Variant-Typen für den Button
 * @typedef {'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark'} ButtonVariant
 */

/**
 * Größen für den Button
 * @typedef {'sm' | 'md' | 'lg'} ButtonSize
 */

/**
 * Eine wiederverwendbare Button-Komponente mit verschiedenen Varianten
 * 
 * @param {Object} props - Komponenteneigenschaften
 * @param {string} props.children - Der Button-Text
 * @param {ButtonVariant} props.variant - Die Variante des Buttons
 * @param {ButtonSize} props.size - Die Größe des Buttons
 * @param {boolean} props.fullWidth - Ob der Button die volle Breite einnehmen soll
 * @param {boolean} props.disabled - Ob der Button deaktiviert ist
 * @param {Function} props.onClick - Die onClick-Funktion
 * @param {string} props.type - Der Button-Typ (button, submit, reset)
 * @param {Object} props.className - Zusätzliche CSS-Klassen
 */
const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  fullWidth = false,
  disabled = false,
  onClick,
  type = 'button',
  className = '',
  ...rest
}) => {
  // Varianten-spezifische Klassen
  const variantClasses = {
    primary: 'bg-primary text-textLight hover:bg-primaryDark',
    secondary: 'bg-gray-200 text-gray-800 hover:bg-gray-300',
    success: 'bg-green-500 text-white hover:bg-green-600',
    danger: 'bg-red-500 text-white hover:bg-red-600',
    warning: 'bg-yellow-500 text-white hover:bg-yellow-600',
    info: 'bg-blue-500 text-white hover:bg-blue-600',
    light: 'bg-gray-100 text-gray-800 hover:bg-gray-200',
    dark: 'bg-gray-800 text-white hover:bg-gray-900',
  };

  // Größen-spezifische Klassen
  const sizeClasses = {
    sm: 'px-2 py-1 text-sm',
    md: 'px-4 py-2',
    lg: 'px-6 py-3 text-lg',
  };

  // Kombinieren aller Klassen
  const buttonClasses = `
    ${variantClasses[variant] || variantClasses.primary}
    ${sizeClasses[size] || sizeClasses.md}
    ${fullWidth ? 'w-full' : ''}
    rounded transition-colors
    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
    ${className}
  `;

  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled}
      {...rest}
    >
      {children}
    </button>
  );
};

export default Button;
